import React from "react";
import { BaseBoxShapeUtil, HTMLContainer, Rectangle2d, TLBaseShape, TLResizeInfo, resizeBox } from "@tldraw/tldraw";

type AddressBoxVariant = "ADM" | "EDDM";

export type CustomAddressBoxShape = TLBaseShape<
  "customAddressBox",
  {
    variant: AddressBoxVariant;
    w: number;
    h: number;
    returnAddress: string;
    displayReturnAddress: string;
    // When false, the return-address block is omitted from the ADM address box.
    // Omitting it keeps the {{return_address}} placeholder out of the exported
    // HTML, so the PDF pipeline never stamps a return address. ADM-only (the EDDM
    // box has no return address). Optional for backward-compat with old designs.
    showReturnAddress?: boolean;
  }
>;

const ADM_WIDTH = 384;
const ADM_HEIGHT = 192;
const EDDM_WIDTH = 256;
const EDDM_HEIGHT = 160;
const STAMP_TEXT = ["PRSRT STD", "US POSTAGE", "PAID", "NEW YORK", "NY", "PERMIT NO", "4736"].join("\n");
// ADM indicia: 4 fixed centered lines (fits at 4pt with letter-spacing 0)
const ADM_STAMP_TEXT = ["PRSRT STD", "U.S. POSTAGE PAID", "NEW YORK NY", "PERMIT NO 4736"].join("\n");

export class CustomAddressBoxUtil extends BaseBoxShapeUtil<CustomAddressBoxShape> {
  static override type = "customAddressBox" as const;
  static override props: Record<string, unknown>;

  override canResize() {
    // Address boxes have postal-spec-required dimensions (ADM_WIDTH / EDDM_WIDTH
    // etc.). Letting users drag-resize them risks producing flyers that fail
    // mailing validation. Tldraw also hides the resize handles automatically
    // when `canResize` returns false. Programmatic resize via `onResize`
    // still works as a safety net (e.g. variant change), so we keep that
    // method intact.
    return false;
  }

  override canBind() {
    return false;
  }

  override canEdit() {
    return false;
  }

  override hideRotateHandle() {
    return true;
  }

  // Enforce minimum size constraints when resizing
  override onResize(shape: CustomAddressBoxShape, info: TLResizeInfo<CustomAddressBoxShape>) {
    const minWidth = shape.props.variant === "ADM" ? ADM_WIDTH : EDDM_WIDTH;
    const minHeight = shape.props.variant === "ADM" ? ADM_HEIGHT : EDDM_HEIGHT;

    // Use resizeBox to get the default resize behavior
    const resized = resizeBox(shape, info);

    // Enforce minimum dimensions
    return {
      ...resized,
      props: {
        ...resized.props,
        w: Math.max(resized.props.w, minWidth),
        h: Math.max(resized.props.h, minHeight),
      },
    };
  }

  getDefaultProps(): CustomAddressBoxShape["props"] {
    return {
      variant: "ADM",
      w: ADM_WIDTH,
      h: ADM_HEIGHT,
      returnAddress: "{{return_address}}",
      displayReturnAddress: "",
      showReturnAddress: true,
    };
  }

  getBounds(shape: CustomAddressBoxShape) {
    return new Rectangle2d({
      x: 0,
      y: 0,
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    });
  }

  component(shape: CustomAddressBoxShape) {
    const { variant, w, h, returnAddress, displayReturnAddress, showReturnAddress } = shape.props;
    const canvasAddress = displayReturnAddress || returnAddress || "{{return_address}}";
    // Default true so designs created before this prop existed keep the return address.
    const showReturn = showReturnAddress !== false;

    return (
      <HTMLContainer id={shape.id} className="tl-address-box" style={{ position: "absolute", width: w, height: h }}>
        {variant === "EDDM" ? renderEddmLayout(w, h) : renderAdmLayout(w, h, canvasAddress, showReturn)}
      </HTMLContainer>
    );
  }

  indicator(shape: CustomAddressBoxShape) {
    return <rect width={shape.props.w} height={shape.props.h} rx={2} ry={2} />;
  }
}

const baseTextStyle: React.CSSProperties = {
  fontFamily: '"Proxima Nova", sans-serif',
  color: "#000",
  lineHeight: "1.2",
  userSelect: "none",
  position: "absolute",
  whiteSpace: "pre-line",
};

const containerStyle = (w: number, h: number): React.CSSProperties => ({
  position: "absolute",
  width: w,
  height: h,
  border: "1px dashed #000",
  backgroundColor: "#fff",
  boxSizing: "border-box",
  userSelect: "none",
});

function renderAdmLayout(width: number, height: number, returnAddress: string, showReturnAddress = true) {
  return (
    <div style={containerStyle(width, height)}>
      {showReturnAddress && (
        <div
          data-export-value="{{return_address}}"
          style={{ ...baseTextStyle, top: 20, left: 16, fontSize: 11, width: 220, fontWeight: 400, letterSpacing: 1 }}
        >
          {returnAddress}
        </div>
      )}
      <div style={{ ...baseTextStyle, top: 80, left: 16, fontSize: 10, fontWeight: 400, letterSpacing: 1 }}>
        {"{{endorse}}"}
      </div>
      <div style={{ ...baseTextStyle, top: 94, left: 16, width: 250, fontSize: 10, fontWeight: 400, letterSpacing: 1 }}>
        {"{{full_name}}"}
      </div>
      <div
        style={{ ...baseTextStyle, top: 108, left: 16, width: 250, fontSize: 10, fontWeight: 400, letterSpacing: 1 }}
      >
        {"{{address}}"}
      </div>
      <div
        style={{ ...baseTextStyle, top: 122, left: 16, width: 250, fontSize: 10, fontWeight: 400, letterSpacing: 1 }}
      >
        {"{{city}}, {{state}} {{zip}}"}
      </div>
      <div
        style={{
          ...baseTextStyle,
          top: 89,
          right: 12,
          width: 80,
          textAlign: "right",
          fontSize: 11,
          fontWeight: 400,
          letterSpacing: 1,
        }}
      >
        {"{{cont_id}}"}
      </div>
      <div
        style={{
          ...baseTextStyle,
          top: 102,
          right: 12,
          width: 80,
          textAlign: "right",
          fontSize: 11,
          fontWeight: 400,
          letterSpacing: 1,
        }}
      >
        {"{{pallet_id}}"}
      </div>
      <div
        style={{
          ...baseTextStyle,
          top: 115,
          right: 12,
          width: 80,
          textAlign: "right",
          fontSize: 11,
          fontWeight: 400,
          letterSpacing: 1,
        }}
      >
        {"{{sequence}}"}
      </div>
      {/* <div
        style={{
          ...baseTextStyle,
          top: 145,
          right: 12,
          width: 80,
          textAlign: "right",
          fontSize: 11,
          fontWeight: 400,
          letterSpacing: 1,
        }}
      >
        {"{{crrt}}"}
      </div> */}
      <div
        data-barcode="imb"
        style={{ position: "absolute", top: 52, left: 16, width: 340, height: 16, overflow: "hidden" }}
      >
        <svg width="340" height="16" viewBox="0 0 340 16" fill="none" role="presentation">
          <path
            d="M0.98291 3.29198H2.23468V6.51084H0.98291V3.29198ZM3.8441 0.251953H5.09587V9.7297H3.8441V0.251953ZM6.61589 0.251953H7.86767V6.51084H6.61589V0.251953ZM9.38769 0.251953H10.6395V9.7297H9.38769V0.251953ZM12.1595 3.29198H13.4113V9.7297H12.1595V3.29198ZM15.0207 3.29198H16.2724V6.51084H15.0207V3.29198ZM17.7925 3.29198H19.0442V9.7297H17.7925V3.29198ZM20.5643 0.251953H21.816V9.7297H20.5643V0.251953ZM23.3361 0.251953H24.5878V6.51084H23.3361V0.251953ZM26.1973 0.251953H27.449V6.51084H26.1973V0.251953ZM28.9691 0.251953H30.2208V9.7297H28.9691V0.251953ZM31.7408 0.251953H32.9926V6.51084H31.7408V0.251953ZM34.5126 3.29198H35.7644V9.7297H34.5126V3.29198ZM37.3738 0.251953H38.6256V6.51084H37.3738V0.251953ZM40.1456 3.29198H41.3974V6.51084H40.1456V3.29198ZM42.9174 0.251953H44.1692V9.7297H42.9174V0.251953ZM45.6892 0.251953H46.941V9.7297H45.6892V0.251953ZM48.5504 3.29198H49.8022V6.51084H48.5504V3.29198ZM51.3222 0.251953H52.574V9.7297H51.3222V0.251953ZM54.094 3.29198H55.3458V6.51084H54.094V3.29198ZM56.8658 0.251953H58.1176V9.7297H56.8658V0.251953ZM59.727 0.251953H60.9788V9.7297H59.727V0.251953ZM62.4988 0.251953H63.7506V9.7297H62.4988V0.251953ZM65.2706 3.29198H66.5224V9.7297H65.2706V3.29198ZM68.0424 0.251953H69.2942V9.7297H68.0424V0.251953ZM70.9036 3.29198H72.1553V6.51084H70.9036V3.29198ZM73.6754 0.251953H74.9271V9.7297H73.6754V0.251953ZM76.4472 0.251953H77.6989V9.7297H76.4472V0.251953ZM79.219 0.251953H80.4707V9.7297H79.219V0.251953ZM82.0801 0.251953H83.3319V9.7297H82.0801V0.251953ZM84.8519 0.251953H86.1037V6.51084H84.8519V0.251953ZM87.6237 0.251953H88.8755V6.51084H87.6237V0.251953ZM90.3955 0.251953H91.6473V9.7297H90.3955V0.251953ZM93.2567 3.29198H94.5085V6.51084H93.2567V3.29198ZM96.0285 0.251953H97.2803V6.51084H96.0285V0.251953ZM98.8003 0.251953H100.052V9.7297H98.8003V0.251953ZM101.572 3.29198H102.824V6.51084H101.572V3.29198ZM104.433 3.29198H105.685V9.7297H104.433V3.29198ZM107.205 3.29198H108.457V6.51084H107.205V3.29198ZM109.977 0.251953H111.229V9.7297H109.977V0.251953ZM112.749 3.29198H114V6.51084H112.749V3.29198ZM115.61 3.29198H116.862V9.7297H115.61V3.29198ZM118.382 3.29198H119.633V6.51084H118.382V3.29198ZM121.153 0.251953H122.405V9.7297H121.153V0.251953ZM123.925 0.251953H125.177V9.7297H123.925V0.251953ZM126.786 3.29198H128.038V6.51084H126.786V3.29198ZM129.558 0.251953H130.81V9.7297H129.558V0.251953ZM132.33 3.29198H133.582V9.7297H132.33V3.29198ZM135.102 3.29198H136.354V6.51084H135.102V3.29198ZM137.963 3.29198H139.215V9.7297H137.963V3.29198ZM140.735 3.29198H141.987V6.51084H140.735V3.29198ZM143.507 3.29198H144.758V6.51084H143.507V3.29198ZM146.278 3.29198H147.53V9.7297H146.278V3.29198ZM149.14 3.29198H150.391V6.51084H149.14V3.29198ZM151.911 0.251953H153.163V6.51084H151.911V0.251953ZM154.683 0.251953H155.935V9.7297H154.683V0.251953ZM157.455 3.29198H158.707V6.51084H157.455V3.29198ZM160.227 0.251953H161.479V9.7297H160.227V0.251953ZM163.088 3.29198H164.34V9.7297H163.088V3.29198ZM165.86 3.29198H167.112V9.7297H165.86V3.29198ZM168.632 0.251953H169.883V9.7297H168.632V0.251953ZM171.403 0.251953H172.655V9.7297H171.403V0.251953ZM174.265 3.29198H175.516V6.51084H174.265V3.29198ZM177.036 0.251953H178.288V9.7297H177.036V0.251953ZM179.808 0.251953H181.06V9.7297H179.808V0.251953Z"
            fill="#231F20"
          />
        </svg>
      </div>
      <Stamp
        style={{ top: 12, right: 12 }}
        width={53}
        height={48}
        fontSize={5.33}
        letterSpacing={0}
        text={ADM_STAMP_TEXT}
      />
    </div>
  );
}

function renderEddmLayout(width: number, height: number) {
  return (
    <div style={containerStyle(width, height)}>
      <div style={{ ...baseTextStyle, top: 52, left: 32, fontSize: 9, fontWeight: 400, letterSpacing: 1 }}>
        *******ECCRWSS EDDM*****
      </div>
      <div style={{ ...baseTextStyle, top: 70, left: 32, fontSize: 9, fontWeight: 400, letterSpacing: 1 }}>
        Residential Customer
      </div>
      <div
        style={{ ...baseTextStyle, top: 80, left: 32, fontSize: 9, fontWeight: 400, letterSpacing: 1 }}
      >{`{{city}}, {{st}} {{zip}}`}</div>
      <div
        style={{
          ...baseTextStyle,
          top: 118,
          left: width - 70,
          width: 60,
          fontSize: 9,
          fontWeight: 400,
          letterSpacing: 1,
        }}
      >
        {"{{crrt}}"}
      </div>
      <Stamp style={{ top: 12, left: width - 70 }} height={50} />
    </div>
  );
}

function Stamp({
  style,
  height = 50,
  width = 50,
  fontSize = 6,
  letterSpacing = 0.8,
  text = STAMP_TEXT,
}: {
  style: React.CSSProperties;
  height?: number;
  width?: number;
  fontSize?: number;
  letterSpacing?: number;
  text?: string;
}) {
  return (
    <div
      style={{
        position: "absolute",
        width,
        height,
        border: "1px solid #000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontWeight: 700,
        fontSize,
        lineHeight: 1,
        letterSpacing,
        fontFamily: '"Proxima Nova", sans-serif',
        whiteSpace: "pre-line",
        ...style,
      }}
    >
      {text}
    </div>
  );
}
